import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PWAThunk } from 'lib/core/data'
import { Callback } from 'lib/types'
import { setCart } from '../cart'
import { Slice, CouponApplySlice } from './types'

export const initialState: Slice = {}

interface ActionData {
  couponCode: string
}

const getSlice = (slice: CouponApplySlice): CouponApplySlice => {
  return slice ?? ({
    isFetching: false,
    isSuccess: false,
    isFailure: false
  })
}

const slice = createSlice({
  name: 'quote/couponApply',
  initialState,
  reducers: {
    request (state, action: PayloadAction<ActionData>) {
      const { couponCode } = action.payload
      const slice = getSlice(state[couponCode])
      slice.isFetching = true
      state[couponCode] = slice
    },

    success (state, action: PayloadAction<ActionData>) {
      const { couponCode } = action.payload
      const slice = getSlice(state[couponCode])
      slice.isFetching = false
      slice.isSuccess = true
      slice.isFailure = false
    },

    failure (state, action: PayloadAction<ActionData>) {
      const { couponCode } = action.payload
      const slice = getSlice(state[couponCode])
      slice.isFetching = false
      slice.isSuccess = false
      slice.isFailure = true
    }
  }
})

export const reducer = slice.reducer

export const { request, success, failure } = slice.actions

export function applyCoupon (cartId: string, couponCode: string, callback?: Callback): PWAThunk {
  return async (dispatch, getState, params) => {
    const { magentoClient } = params
    dispatch(request({ couponCode }))
    try {
      const response = await magentoClient.getClient().quote.couponApply({
        cartId,
        couponCode
      })

      const { data } = response
      const { applyCouponToCart } = data
      const { cart } = applyCouponToCart

      if (cart) {
        dispatch(setCart({
          cartId,
          data: cart,
          merge: true
        }))
        dispatch(success({ couponCode }))
        callback?.onSuccess?.()
      } else {
        dispatch(failure({ couponCode }))
        callback?.onFailure?.({
          error: new Error(`[Coupon Apply] Unexpected payload: ${JSON.stringify(response)}`),
          extraData: {
            couponCode
          }
        })
      }

      return response
    } catch (e) {
      dispatch(failure({ couponCode }))
      callback?.onFailure?.({
        error: new Error(`[Coupon Apply] ${e.message}`),
        extraData: {
          couponCode
        }
      })
    }
  }
}
